/**
 * InMocean tenant-specific permission groups.
 *
 * Per CLAUDE.md §10 (C-model architecture), tenant-specific configuration
 * lives in src/tenants/{tenantId}/ — not in shared platform services.
 *
 * Consumed by src/shared/services/PermissionService.js via static import
 * + lookup by tenantId. Adding a new tenant = create their own permissions.js
 * + register it in PermissionService.TENANT_PERMISSIONS map.
 *
 * Note: tenant ID dual-keying (`company_inmocean` legacy + `inmocean`
 * post-CHA-82) is handled at the PermissionService registry level,
 * not here. This file knows only InMocean's permission shape.
 */

export const INMOCEAN_TENANT_PERMISSIONS = {
    admin: {
        pages: ['dashboard', 'analytics', 'user_management', 'settings', 'data_upload', 'reports', 'workflow', 'duty_verification'],
        features: ['file_upload', 'data_view', 'user_management', 'analytics', 'reports', 'workflow_system', 'duty_verification'],
        actions: ['read', 'write', 'delete', 'approve'],
        special_access: ['missing_data_reports', 'cost_reports', 'broker_data']
    },
    user: {
        pages: ['dashboard', 'data_view', 'reports', 'workflow'],
        features: ['data_view', 'reports', 'workflow_participation'],
        actions: ['read', 'write'],
        special_access: ['own_reports', 'workflow_tasks']
    }
};

export const INMOCEAN_GROUP_PERMISSIONS = {
    // Finance Team - Access to financial reports and cost data
    finance_team: {
        name: 'Finance Team',
        description: 'Access to financial reports, cost allocation, and budget data',
        pages: ['dashboard', 'reports', 'cost_reports', 'budget_analysis'],
        features: ['data_view', 'financial_reports', 'cost_analysis', 'budget_tracking'],
        actions: ['read', 'write'],
        color: 'bg-green-100 text-green-800'
    },

    // Operations Team - Access to operational data and workflow
    operations_team: {
        name: 'Operations Team',
        description: 'Access to operational data, workflow management, and production reports',
        pages: ['dashboard', 'reports', 'workflow', 'production_data', 'duty_verification'],
        features: ['data_view', 'workflow_system', 'production_reports', 'duty_verification'],
        actions: ['read', 'write', 'approve'],
        color: 'bg-blue-100 text-blue-800'
    },

    // Analytics Team - Access to analytics and reporting tools
    analytics_team: {
        name: 'Analytics Team',
        description: 'Access to analytics dashboard, data analysis, and reporting tools',
        pages: ['dashboard', 'analytics', 'reports', 'data_analysis', 'performance_metrics'],
        features: ['data_view', 'analytics', 'reports', 'data_analysis', 'performance_tracking'],
        actions: ['read', 'write'],
        color: 'bg-purple-100 text-purple-800'
    },

    // Data Entry Team - Access to data upload and entry
    data_entry_team: {
        name: 'Data Entry Team',
        description: 'Access to data upload, entry forms, and basic data viewing',
        pages: ['dashboard', 'data_upload', 'data_entry', 'data_view'],
        features: ['file_upload', 'data_entry', 'data_view', 'form_submission'],
        actions: ['read', 'write'],
        color: 'bg-yellow-100 text-yellow-800'
    },

    // View Only Team - Read-only access to basic data
    view_only_team: {
        name: 'View Only Team',
        description: 'Read-only access to basic dashboard and reports',
        pages: ['dashboard', 'data_view', 'reports'],
        features: ['data_view', 'reports'],
        actions: ['read'],
        color: 'bg-gray-100 text-gray-800'
    }
};
